/**
 * Business Inquiry Form Component
 * 업체 문의 폼 (Business Premium 전용)
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Send } from "lucide-react";

interface Product {
  id: number;
  name: string;
  price_cents: number | null;
}

interface BusinessInquiryFormProps {
  businessId: string;
  businessName: string;
  products: Product[];
}

export function BusinessInquiryForm({
  businessId,
  businessName,
  products,
}: BusinessInquiryFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [inquiryType, setInquiryType] = useState<"inquiry" | "order" | "reservation">("inquiry");
  const [customerName, setCustomerName] = useState(user?.displayName || "");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [pickupPreferred, setPickupPreferred] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [options, setOptions] = useState<Record<string, string>>({});
  const [reservationDate, setReservationDate] = useState("");
  const [reservationTime, setReservationTime] = useState("");

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!customerName.trim() || !message.trim()) {
        throw new Error("이름과 문의 내용을 입력해주세요");
      }

      const body: any = {
        type: inquiryType,
        customer_name: customerName,
        customer_phone: customerPhone || null,
        customer_email: customerEmail || null,
        message: message.trim(),
      };

      if (inquiryType === "order" && selectedProductId) {
        body.product_id = selectedProductId;
        body.quantity = quantity;
        body.pickup_preferred = pickupPreferred;
        body.delivery_address = pickupPreferred ? null : deliveryAddress;
        if (Object.keys(options).length > 0) {
          body.options_json = JSON.stringify(options);
        }
      }

      if (inquiryType === "reservation") {
        if (!reservationDate || !reservationTime) {
          throw new Error("예약 날짜와 시간을 선택해주세요");
        }
        body.reservation_date = reservationDate;
        body.reservation_time = reservationTime;
      }

      return apiRequest("POST", `/api/businesses/${businessId}/inquiries`, body);
    },
    onSuccess: () => {
      toast({
        title: "문의가 전송되었습니다",
        description: `${businessName}에게 문의가 전달되었습니다`,
      });
      setMessage("");
      setCustomerPhone("");
      setReservationDate("");
      setReservationTime("");
      setSelectedProductId(null);
      setQuantity(1);
      setPickupPreferred(false);
      setDeliveryAddress("");
      setOptions({});
    },
    onError: (error: Error) => {
      toast({
        title: "문의 전송 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      {/* Inquiry Type */}
      <div className="space-y-2">
        <Label>문의 유형</Label>
        <Select
          value={inquiryType}
          onValueChange={(value: "inquiry" | "order" | "reservation") =>
            setInquiryType(value)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inquiry">일반 문의</SelectItem>
            <SelectItem value="order">주문하기</SelectItem>
            <SelectItem value="reservation">예약하기</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Product Selection (for orders) */}
      {inquiryType === "order" && products.length > 0 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>상품 선택 *</Label>
            <Select
              value={selectedProductId?.toString() || ""}
              onValueChange={(value) => {
                setSelectedProductId(parseInt(value));
                setOptions({}); // Reset options when product changes
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="상품을 선택하세요" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id.toString()}>
                    {product.name} - ${((product.price_cents || 0) / 100).toFixed(0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedProductId && (
            <>
              <div className="space-y-2">
                <Label>수량 *</Label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              {/* Options (size, color, etc.) */}
              <div className="space-y-2">
                <Label>옵션 (선택)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="예: 사이즈 (L)"
                    value={options.size || ""}
                    onChange={(e) =>
                      setOptions({ ...options, size: e.target.value })
                    }
                  />
                  <Input
                    placeholder="예: 색상 (빨강)"
                    value={options.color || ""}
                    onChange={(e) =>
                      setOptions({ ...options, color: e.target.value })
                    }
                  />
                </div>
              </div>
              {/* Delivery/Pickup */}
              <div className="space-y-2">
                <Label>수령 방법</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={pickupPreferred}
                      onChange={() => setPickupPreferred(true)}
                    />
                    <span>픽업</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!pickupPreferred}
                      onChange={() => setPickupPreferred(false)}
                    />
                    <span>배송</span>
                  </label>
                </div>
                {!pickupPreferred && (
                  <div className="space-y-2 mt-2">
                    <Label>배송 주소 *</Label>
                    <Textarea
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="배송 주소를 입력해주세요"
                      rows={2}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Reservation Date/Time (for reservations) */}
      {inquiryType === "reservation" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>예약 날짜 *</Label>
            <Input
              type="date"
              value={reservationDate}
              onChange={(e) => setReservationDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>예약 시간 *</Label>
            <Input
              type="time"
              value={reservationTime}
              onChange={(e) => setReservationTime(e.target.value)}
              required
            />
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>이름 *</Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>전화번호</Label>
          <Input
            type="tel"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="0412345678"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>이메일</Label>
        <Input
          type="email"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
        />
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label>문의 내용 *</Label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="문의 내용을 입력해주세요"
          required
        />
      </div>

      {/* Submit Button */}
      <Button
        className="w-full"
        onClick={() => submitMutation.mutate()}
        disabled={submitMutation.isPending || !customerName.trim() || !message.trim()}
      >
        {submitMutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            전송 중...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            문의 보내기
          </>
        )}
      </Button>
    </div>
  );
}
